import { updateSessionById } from '@/pages/api/scribeAPI';
import { toast } from '@heidi/design-system/toast';

import { GET_EHR_APPOINTMENTS } from '@/hooks/query/integrations/useQueryEHRAppointments';
import { type SearchAppointmentResponse } from '@heidi/integrations/ehr/contracts';
import {
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
  type SessionResponse,
} from '@heidi/session/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { get } from 'lodash-es';

type Props = {
  sessionId: string;
  EHRApptId: string;
  createdTimeLocal: string;
  ehrProvider?: string;
  ehrPatientId?: string;
  ehrEncounterId?: string;
};

const UPDATE_SESSION_EHR_APPOINTMENT_ID = 'UpdateSessionEHRAppointmentId';

export const useMutateUpdateSessionEHRAppointmentId = (date?: Date) => {
  const queryClient = useQueryClient();

  const dateInput = date ? format(date, 'yyyy-MM-dd') : undefined;

  return useMutation({
    mutationKey: [UPDATE_SESSION_EHR_APPOINTMENT_ID],
    mutationFn: async ({
      sessionId,
      EHRApptId,
      createdTimeLocal,
      ehrProvider,
      ehrPatientId,
      ehrEncounterId,
    }: Props) => {
      return await updateSessionById(sessionId, {
        ehr_appt_id: EHRApptId,
        created_time_local: createdTimeLocal,
        ehr_provider: ehrProvider,
        ...(ehrPatientId && { ehr_patient_id: ehrPatientId }),
        ...(ehrEncounterId && { ehr_encounter_id: ehrEncounterId }),
      });
    },
    onSuccess: (res) => {
      if (!res?.data) {
        return;
      }
      const data = res.data;
      const {
        session_id: sessionId,
        ehr_appt_id,
        created_time_local,
        ehr_provider: ehrProviderFromData,
      } = data;
      queryClient.setQueryData<QuerySessionResponse>(
        [GET_SESSION_BY_ID, { sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          const newData: SessionResponse = {
            ...oldData,
            ehr_appt_id,
            created_time_local,
            ehr_provider: oldData.patient_profile?.ehr ?? ehrProviderFromData,
          };
          return newData;
        }
      );
    },
    onMutate: async ({ EHRApptId, sessionId }: Props) => {
      if (!date) {
        return;
      }
      await queryClient.cancelQueries({
        queryKey: [GET_EHR_APPOINTMENTS, { date: dateInput }],
      });
      const previousAppointments = queryClient.getQueryData([
        GET_EHR_APPOINTMENTS,
        { date: dateInput },
      ]);

      queryClient.setQueryData<SearchAppointmentResponse>(
        [GET_EHR_APPOINTMENTS, { date: dateInput }],
        (oldData) => {
          if (!oldData?.appointments) {
            return oldData;
          }

          const newAppointments = oldData.appointments.map((appointment) => {
            if (appointment.record_id === EHRApptId) {
              return {
                ...appointment,
                session_id: sessionId,
              };
            }
            return appointment;
          });

          return { appointments: newAppointments };
        }
      );

      return { previousAppointments };
    },

    onError: (error) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_EHR_APPOINTMENTS, { date: dateInput }],
      });

      if (error instanceof AxiosError) {
        toast({
          tone: 'destructive',
          title: 'Failed to update session',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          description: get(error.response, 'data.detail.message', error.message),
        });
      }
    },
  });
};
